/**
 * BTRA — Business Threat & Risk Assessment graph.
 *
 * SVG radial layout:
 *   Centre          Company node (colour = risk tier)
 *   Ring 1 (r=145)  Directors (red border on sanctions hit)
 *   Ring 2 (r=235)  Threat signals: sanctions hits, regulatory flags, breach count
 *   Ring 3 (r=310)  Attack surface: domain, email posture, subdomains
 */
import { useMemo, useRef, useState } from 'react';
import type {
  CompanyAttackSurface,
  CompanyRecord,
  SanctionsHit,
} from '@/types/profiler';

const W = 680;
const H = 680;
const CX = W / 2;
const CY = H / 2;

const R1 = 145;   // directors
const R2 = 235;   // threats
const R3 = 310;   // attack surface

// ── colour palette ─────────────────────────────────────────────────────────────

const TIER_C: Record<string, string> = {
  CRITICAL: '#FF1F1F',
  HIGH:     '#FF6B1A',
  MEDIUM:   '#F0B429',
  LOW:      '#00C853',
};

const GRADE_C: Record<string, string> = {
  A: '#00C853',
  B: '#69B578',
  C: '#F0B429',
  D: '#FF6B1A',
  F: '#FF1F1F',
};

// ── polar helpers ──────────────────────────────────────────────────────────────

function polar(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = (angleDeg - 90) * (Math.PI / 180);
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function ringPositions(count: number, r: number, offsetDeg = 0) {
  if (count === 0) return [];
  const step = 360 / count;
  return Array.from({ length: count }, (_, i) => polar(CX, CY, r, offsetDeg + i * step));
}

// ── node types ────────────────────────────────────────────────────────────────

type NodeKind = 'company' | 'director' | 'sanction' | 'breach' | 'regulatory' | 'domain' | 'email_grade' | 'subdomain';

interface GNode {
  id: string;
  kind: NodeKind;
  label: string;
  sub?: string;
  x: number;
  y: number;
  r: number;
  fill: string;
  stroke: string;
  strokeWidth: number;
  pulse?: boolean;   // animated ring
  textColor?: string;
}

// ── label truncation ──────────────────────────────────────────────────────────

function trunc(s: string, n: number) {
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// ── SVG node renderer ─────────────────────────────────────────────────────────

function Node({ node, onClick, active }: {
  node: GNode;
  onClick?: () => void;
  active?: boolean;
}) {
  return (
    <g
      onClick={onClick}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
    >
      {/* Pulse ring for high-risk nodes */}
      {node.pulse && (
        <circle cx={node.x} cy={node.y} r={node.r + 8} fill="none" stroke={node.stroke} strokeWidth={1} opacity={0.25}>
          <animate attributeName="r" values={`${node.r + 5};${node.r + 16};${node.r + 5}`} dur="2.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.35;0.0;0.35" dur="2.5s" repeatCount="indefinite" />
        </circle>
      )}

      {/* Selection halo */}
      {active && (
        <circle cx={node.x} cy={node.y} r={node.r + 5} fill="none" stroke="#FFFFFF" strokeWidth={1.5} opacity={0.4} />
      )}

      {/* Main circle */}
      <circle
        cx={node.x} cy={node.y} r={node.r}
        fill={node.fill}
        stroke={node.stroke}
        strokeWidth={node.strokeWidth}
      />

      {/* Label */}
      <text
        x={node.x} y={node.y - 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize={node.kind === 'company' ? 11 : 8}
        fontFamily="monospace"
        fontWeight={node.kind === 'company' ? 800 : 600}
        fill={node.textColor ?? '#DCE8F5'}
      >
        {trunc(node.label, node.kind === 'company' ? 16 : 10)}
      </text>
      {node.sub && (
        <text
          x={node.x} y={node.y + 9}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={7}
          fontFamily="monospace"
          fill="#5A7A9A"
        >
          {trunc(node.sub, 12)}
        </text>
      )}
    </g>
  );
}

// ── edge renderer ─────────────────────────────────────────────────────────────

function Edge({ x1, y1, x2, y2, color, dashed }: {
  x1: number; y1: number; x2: number; y2: number;
  color: string; dashed?: boolean;
}) {
  return (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color}
      strokeWidth={1}
      strokeOpacity={0.35}
      strokeDasharray={dashed ? '4 3' : undefined}
    />
  );
}

// ── legend ────────────────────────────────────────────────────────────────────

function Legend() {
  const items: Array<{ color: string; label: string }> = [
    { color: '#FF1F1F', label: 'Critical / Sanctions hit' },
    { color: '#FF6B1A', label: 'Director / High risk' },
    { color: '#2979FF', label: 'Attack surface / Domain' },
    { color: '#F0B429', label: 'Regulatory / Breach' },
    { color: '#00C853', label: 'Clean posture' },
  ];
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginTop: 8 }}>
      {items.map(it => (
        <div key={it.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: it.color, flexShrink: 0 }} />
          <span style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace' }}>{it.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── tooltip ───────────────────────────────────────────────────────────────────

function Tooltip({ node }: { node: GNode | null }) {
  if (!node) return null;
  return (
    <div
      style={{
        position: 'absolute', top: 8, right: 8,
        background: '#090D13',
        border: `1px solid ${node.stroke}50`,
        borderRadius: 8,
        padding: '10px 14px',
        minWidth: 180,
        zIndex: 10,
        pointerEvents: 'none',
      }}
    >
      <p style={{ color: '#5A7A9A', fontSize: 8, fontFamily: 'monospace', letterSpacing: '0.1em', marginBottom: 4 }}>
        {node.kind.toUpperCase().replace('_', ' ')}
      </p>
      <p style={{ color: '#DCE8F5', fontSize: 11, fontWeight: 700, wordBreak: 'break-all' }}>{node.label}</p>
      {node.sub && <p style={{ color: '#5A7A9A', fontSize: 9, marginTop: 2 }}>{node.sub}</p>}
    </div>
  );
}

// ── main component ────────────────────────────────────────────────────────────

interface BTRAGraphProps {
  companyName: string;
  riskTier: string;
  companies: CompanyRecord[];
  sanctionHits: SanctionsHit[];
  exposure?: CompanyAttackSurface;
  breachCount?: number;
  breachSources?: string[];
}

export function BTRAGraph({
  companyName,
  riskTier,
  companies,
  sanctionHits,
  exposure,
  breachCount = 0,
  breachSources = [],
}: BTRAGraphProps) {
  const [active, setActive] = useState<GNode | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const tierColor = TIER_C[riskTier] ?? '#5A7A9A';

  // ── Build nodes ───────────────────────────────────────────────────────────

  const nodes = useMemo<GNode[]>(() => {
    const ns: GNode[] = [];

    // ── Centre: Company ─────────────────────────────────────────────────────
    ns.push({
      id: 'company',
      kind: 'company',
      label: companyName,
      sub: riskTier,
      x: CX, y: CY,
      r: 40,
      fill: `${tierColor}22`,
      stroke: tierColor,
      strokeWidth: 2.5,
      pulse: riskTier === 'CRITICAL' || riskTier === 'HIGH',
      textColor: tierColor,
    });

    // ── Ring 1: Directors ───────────────────────────────────────────────────
    const allDirectors = Array.from(new Set(
      companies.flatMap(c => c.co_directors ?? [])
    )).slice(0, 10);

    const sanctionedNames = new Set(
      sanctionHits.map(h => h.caption.toLowerCase())
    );

    const dirPositions = ringPositions(allDirectors.length, R1, 15);
    allDirectors.forEach((name, i) => {
      const isSanctioned = sanctionedNames.has(name.toLowerCase());
      ns.push({
        id: `dir-${i}`,
        kind: 'director',
        label: name,
        x: dirPositions[i].x,
        y: dirPositions[i].y,
        r: 22,
        fill: isSanctioned ? '#FF1F1F18' : '#0F1928',
        stroke: isSanctioned ? '#FF1F1F' : '#FF6B1A',
        strokeWidth: isSanctioned ? 2 : 1.5,
        pulse: isSanctioned,
        sub: isSanctioned ? 'SANCTIONED' : undefined,
        textColor: isSanctioned ? '#FF1F1F' : '#DCE8F5',
      });
    });

    // ── Ring 2: Threat signals ──────────────────────────────────────────────
    const threatNodes: Array<Omit<GNode, 'x' | 'y'>> = [];

    if (sanctionHits.length > 0) {
      threatNodes.push({
        id: 'sanctions',
        kind: 'sanction',
        label: `${sanctionHits.length} HITS`,
        sub: 'OpenSanctions',
        r: 24,
        fill: '#FF1F1F18',
        stroke: '#FF1F1F',
        strokeWidth: 2,
        pulse: true,
        textColor: '#FF1F1F',
      });
    }

    if (breachCount > 0) {
      threatNodes.push({
        id: 'breach',
        kind: 'breach',
        label: `${breachCount} BREACHES`,
        sub: breachSources.slice(0, 2).join(', ') || 'Credential DBs',
        r: 24,
        fill: '#FF6B1A18',
        stroke: '#FF6B1A',
        strokeWidth: 1.5,
        pulse: breachCount >= 5,
        textColor: '#FF6B1A',
      });
    }

    const struckOff = companies.filter(c => c.status === 'STRUCK_OFF').length;
    if (struckOff > 0) {
      threatNodes.push({
        id: 'regulatory',
        kind: 'regulatory',
        label: `${struckOff} STRUCK OFF`,
        sub: 'MCA21 Registry',
        r: 22,
        fill: '#F0B42918',
        stroke: '#F0B429',
        strokeWidth: 1.5,
        textColor: '#F0B429',
      });
    }

    const threatPos = ringPositions(threatNodes.length, R2, -30);
    threatNodes.forEach((n, i) => {
      ns.push({ ...n, x: threatPos[i].x, y: threatPos[i].y });
    });

    // ── Ring 3: Attack surface ──────────────────────────────────────────────
    const atkNodes: Array<Omit<GNode, 'x' | 'y'>> = [];

    if (exposure) {
      // Domain node
      atkNodes.push({
        id: 'domain',
        kind: 'domain',
        label: exposure.domain,
        sub: `${exposure.subdomain_count} subdomains`,
        r: 24,
        fill: '#2979FF18',
        stroke: '#2979FF',
        strokeWidth: 1.5,
        textColor: '#2979FF',
      });

      // Email posture grade
      if (exposure.email_posture) {
        const g = exposure.email_posture.grade;
        const gc = GRADE_C[g] ?? '#5A7A9A';
        atkNodes.push({
          id: 'email_grade',
          kind: 'email_grade',
          label: `Grade ${g}`,
          sub: 'Email security',
          r: 22,
          fill: `${gc}18`,
          stroke: gc,
          strokeWidth: g === 'A' ? 1 : 2,
          pulse: g === 'F' || g === 'D',
          textColor: gc,
        });
      }

      // Sensitive subdomains
      if (exposure.sensitive_subdomain_count > 0) {
        atkNodes.push({
          id: 'sensitive_subs',
          kind: 'subdomain',
          label: `${exposure.sensitive_subdomain_count} EXPOSED`,
          sub: 'Sensitive hosts',
          r: 22,
          fill: '#FF6B1A18',
          stroke: '#FF6B1A',
          strokeWidth: 1.5,
          pulse: exposure.sensitive_subdomain_count >= 3,
          textColor: '#FF6B1A',
        });
      }
    }

    const atkPos = ringPositions(atkNodes.length, R3, 60);
    atkNodes.forEach((n, i) => {
      ns.push({ ...n, x: atkPos[i].x, y: atkPos[i].y });
    });

    return ns;
  }, [companyName, riskTier, companies, sanctionHits, exposure, breachCount, breachSources, tierColor]);

  // ── Build edges ───────────────────────────────────────────────────────────

  const edges = useMemo(() => {
    const center = nodes.find(n => n.id === 'company');
    if (!center) return [];

    return nodes
      .filter(n => n.id !== 'company')
      .map(n => ({
        key: n.id,
        x1: center.x, y1: center.y,
        x2: n.x, y2: n.y,
        color: n.stroke,
        dashed: n.kind === 'subdomain' || n.kind === 'email_grade',
      }));
  }, [nodes]);

  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        background: '#060A10',
        border: '1px solid #182030',
        borderRadius: 12,
        overflow: 'hidden',
        position: 'relative',
      }}>
        {/* Header */}
        <div style={{
          borderBottom: '1px solid #182030',
          padding: '10px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: tierColor }} />
            <span style={{ color: '#5A7A9A', fontSize: 9, fontFamily: 'monospace', letterSpacing: '0.15em' }}>
              BUSINESS THREAT & RISK ASSESSMENT — EXPOSURE GRAPH
            </span>
          </div>
          <span
            style={{
              background: `${tierColor}18`,
              color: tierColor,
              border: `1px solid ${tierColor}40`,
              fontSize: 8,
              fontFamily: 'monospace',
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 99,
            }}
          >
            {riskTier}
          </span>
        </div>

        {/* SVG canvas */}
        <div style={{ position: 'relative' }}>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            width="100%"
            style={{ display: 'block', background: '#060A10' }}
            onClick={() => setActive(null)}
          >
            {/* Radial grid rings */}
            {[R1, R2, R3].map((r, i) => (
              <circle
                key={r}
                cx={CX} cy={CY} r={r}
                fill="none"
                stroke="#182030"
                strokeWidth={1}
                strokeDasharray={i === 0 ? undefined : '3 4'}
              />
            ))}

            {/* Edges */}
            {edges.map(e => (
              <Edge key={e.key} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} color={e.color} dashed={e.dashed} />
            ))}

            {/* Nodes */}
            {nodes.map(n => (
              <Node
                key={n.id}
                node={n}
                active={active?.id === n.id}
                onClick={() => setActive(prev => prev?.id === n.id ? null : n)}
              />
            ))}
          </svg>

          {/* Tooltip */}
          <Tooltip node={active} />
        </div>

        {/* Footer legend */}
        <div style={{ borderTop: '1px solid #182030', padding: '8px 16px' }}>
          <Legend />
        </div>
      </div>
    </div>
  );
}
